import type { ChangeEvent, FC, FocusEvent, ReactNode } from 'react'
import { useRef, useState } from 'react'
import {
  FastField,
  type FastFieldProps,
  type FieldInputProps,
  type GenericFieldHTMLAttributes,
  getIn,
  type FormikProps,
} from 'formik'
import { isIOS } from 'react-device-detect'
import { Box, TextField, type TextFieldProps, Typography } from '@dhl/theme'
import { useFocusOnFirstError } from '@dhl/use-focus-on-first-error'

export type TextFieldInputProps = Omit<TextFieldProps, keyof FieldInputProps<string> | 'error' | 'inputRef'> & {
  name: string
  transformBeforeChange?: (e: ChangeEvent<HTMLInputElement>) => string
  hideMaxLength?: boolean
}

// This weird looking calculation is because Enters (CRLF) are counted differently in iOS with regard to the maxLength property
// => They are 2 characters instead of 1.
const CRLFRegEx = /\r(?!\n)|\n(?!\r)/g
const CRLFReplacer = isIOS ? 'rn' : 'r'

function calculateLength(value: string): number {
  return value.replace(CRLFRegEx, CRLFReplacer).length
}

type FieldHtmlAttributesWithFormik<V> = GenericFieldHTMLAttributes & {
  formik: FormikProps<V>
  readOnly?: boolean
  name: string | string[]
  label?: ReactNode
}

const shouldUpdate = <V,>(next: FieldHtmlAttributesWithFormik<V>, current: FieldHtmlAttributesWithFormik<V>) =>
  next.name !== current.name ||
  next.label !== current.label ||
  next.required !== current.required ||
  next.disabled !== current.disabled ||
  next.readOnly !== current.readOnly ||
  next.formik.isSubmitting !== current.formik.isSubmitting ||
  Object.keys(next).length !== Object.keys(current).length ||
  getIn(next.formik.values, current.name) !== getIn(current.formik.values, current.name) ||
  getIn(next.formik.errors, current.name) !== getIn(current.formik.errors, current.name) ||
  getIn(next.formik.touched, current.name) !== getIn(current.formik.touched, current.name)

export const TextFieldInput: FC<TextFieldInputProps> = ({
  name,
  helperText,
  variant,
  autoComplete,
  transformBeforeChange,
  hideMaxLength,
  ...props
}: TextFieldInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [random] = useState(new Date().getTime())

  useFocusOnFirstError(name, inputRef, inputRef)

  // Chrome ignores autoComplete="off" ...
  const fieldName = autoComplete === 'off' ? `${name}-${random}` : name
  const fieldAutocomplete = autoComplete === 'off' ? `off-${random}` : autoComplete

  // TypeScript complains about maxLength not being a valid property
  // typescript: Property 'maxLength' does not exist on type 'SlotProps<ElementType<InputBaseComponentProps | undefined, keyof IntrinsicElements>, {}, BaseTextFieldProps>'.
  // @ts-expect-error
  const maxLength = props.slotProps?.htmlInput?.maxLength

  return (
    <FastField {...props} name={name} shouldUpdate={shouldUpdate}>
      {({ field, meta, form }: FastFieldProps) => (
        <Box
          sx={{
            position: 'relative',
            width: props.fullWidth ? '100%' : 'auto',
          }}
        >
          <TextField
            {...props}
            {...field}
            name={fieldName}
            id={fieldName}
            autoComplete={fieldAutocomplete}
            variant={variant || 'outlined'}
            inputRef={inputRef}
            onBlur={(e: FocusEvent<HTMLInputElement>) => {
              // Update the touched flag, but don't trigger a validation. If needed, we will trigger a validation
              // on the `setValue`. We don't want to trigger two validations. This is also the reason
              // why we don't call `field.onBlur` here.
              form.setFieldTouched(name, true, false)

              // Update the value with the trimmed input and trigger a validation if validateOnBlur equals true
              form.setFieldValue(name, e.target.value?.trim(), form.validateOnBlur)
            }}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              // Restore name attribute. Needed if the name was randomized to fix autoComplete="off"
              e.target.name = name

              // Only replace the target value if the method is actually defined. Setting the target value
              // to a new value causes a strange issue in iOS Safari. When you move the cursor to another spot
              // in something you typed and then start typing again, the cursor is moved to the end of the input.
              // Not an ideal fix, but at least this significantly reduces the change of it occurring, since this
              // prop is only used in a few places.
              if (transformBeforeChange) {
                e.target.value = transformBeforeChange(e)
              }

              field.onChange(e)
            }}
            error={!!meta.error && meta.touched}
            helperText={meta.touched && meta.error?.trim() ? meta.error : helperText}
          />
          {!hideMaxLength && maxLength && (
            <Typography
              variant="body2"
              sx={(theme) => ({
                position: 'absolute',
                bottom: theme.spacing(meta.error ? 3 : 0.5),
                right: theme.spacing(0.5),
              })}
            >
              {`${field.value ? calculateLength(field.value) : 0}/${maxLength}`}
            </Typography>
          )}
        </Box>
      )}
    </FastField>
  )
}
